import { partnerTerms, stripFrontmatter } from "@biteasy/legal";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getRouteApi, useRouter } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import Markdown from "react-markdown";
import { AuthLayout } from "@/components/catalyst/AuthLayout";
import { Button } from "@/components/catalyst/Button";
import { Checkbox, CheckboxField } from "@/components/catalyst/Checkbox";
import { Label } from "@/components/catalyst/Fieldset";
import { Heading, Subheading } from "@/components/catalyst/Heading";
import { Logo } from "@/components/Logo";
import { orpc } from "@/orpc/client";
import { buildTermsStatusInput } from "./util";

const routeApi = getRouteApi("/partner");
const partnerTermsContent = stripFrontmatter(partnerTerms);

interface PartnerTermsGateProps {
	children: ReactNode;
}

/**
 * Gate component that checks if the partner has accepted global terms of service.
 * Uses route context to determine flow type and access prefetched data.
 */
export function PartnerTermsGate({ children }: PartnerTermsGateProps) {
	const router = useRouter();
	const queryClient = useQueryClient();
	const { flow } = routeApi.useRouteContext();
	const terms = routeApi.useLoaderData();
	const [termsAccepted, setTermsAccepted] = useState(false);

	const termsStatusInput = buildTermsStatusInput(flow);

	const { mutate: acceptTerms, isPending: isAccepting } = useMutation(
		orpc.partners.acceptTerms.mutationOptions({
			onSuccess: async () => {
				if (termsStatusInput) {
					// Refetch to get fresh data before router re-runs loader
					await queryClient.refetchQueries({
						queryKey: orpc.partners.getTermsStatus.queryKey({
							input: termsStatusInput,
						}),
					});
				}
				await router.invalidate();
			},
		}),
	);

	if (flow.type === "none" || terms === null || terms?.acceptedTermsAt) {
		console.log("terms accepted or no flow");
		return <>{children}</>;
	}

	// Partner needs to accept terms
	const handleAccept = () => {
		if (!termsAccepted || !termsStatusInput) {
			window.location.href = "https://biteasy.co/";
			return;
		}
		acceptTerms(termsStatusInput);
	};

	return (
		<AuthLayout>
			<div className="flex flex-col gap-6 w-full max-w-2xl">
				<div className="flex flex-col items-center gap-4">
					<Logo />
					<div className="flex flex-col gap-2 text-center">
						<Heading>Partner Agreement</Heading>
						<Subheading>
							Hi {terms?.partnerName}, please review and accept the terms to
							continue.
						</Subheading>
					</div>

					<div className="h-96 w-full overflow-y-auto border border-base-300 rounded-lg p-6 text-left bg-base-200/50 prose prose-sm prose-invert max-w-none prose-headings:text-base-content prose-p:text-base-content/80 prose-strong:text-base-content prose-li:text-base-content/80 prose-li:marker:text-accent prose-a:text-accent prose-a:no-underline hover:prose-a:underline">
						<Markdown>{partnerTermsContent}</Markdown>
					</div>

					<CheckboxField className="w-full">
						<Checkbox
							checked={termsAccepted}
							onChange={setTermsAccepted}
							color="indigo"
						/>
						<Label className="font-semibold">
							I have read and agree to the Partner Terms of Service
						</Label>
					</CheckboxField>

					<Button
						color="dark/white"
						disabled={!termsAccepted || isAccepting}
						onClick={handleAccept}
						className="w-full"
					>
						{isAccepting ? "Processing..." : "Accept & Continue"}
					</Button>
				</div>
			</div>
		</AuthLayout>
	);
}
